import React, { useState, useEffect } from "react";
import { HeaderComponent, ButtonComponent, StatusTag, CountryFlag, useConfirm } from '@comps/components';
import { makeRequest, parseJsonList } from '@utils/helpers';
import { getUserCaseDetailReq, takeUserCasReq, acceptCaseReq, getDownloadFilesReq, completeCaseReq, resolveMissingDataReq, getCaseContainersReq, addCaseContainerReq, deleteCaseContainerReq} from '../requests';
import { useSelector } from 'react-redux';
import moment from "moment";
import { Collapse, Row, Col, Tooltip, Typography, Upload } from "antd";
import toast from "@comps/toast";
import { Icon } from '@iconify/react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ScanImage from "@assets/images/icons/stl.png";
import { handleDownload, checkPermission, isTechnicianRole, isAdmin } from "@comps/commonFunctions";
import { fileUrl } from "@utils/axios";
import { safeHtml } from "@utils/safeHtml";
import SafePdfComponent from "./SafePdfComponent";
import RejectCaseComponent from "./RejectCaseComponent";
import AmendCaseComponent from "./AmendCaseComponent";
import CancelCaseComponent from "./CancelCaseComponent";
import ViewImagesModal from "./ViewImages";
import View3DSetup from "./View3DSetup";
import UpdateCase from "@mods/caseManagement/cases/components/UpdateCase";


import TeethIcon from "./TeethIcon"
import CaseDetailSkeleton from "./CaseDetailSkeleton";
import CaseReasonBanner from "./CaseReasonBanner";
import CaseStlDelivery from "./CaseStlDelivery";
import BaseError from "@comps/layout/BaseError";
import { useCaseCounts } from "../../../containers/CaseCounts";
import "../case.css"
import "../caseDetail.css"

const { Text } = Typography;

// ---------------------------------------------------------------------
// Small presentational helpers (pure layout/styling — no business logic)
// ---------------------------------------------------------------------
const SectionCard = ({ icon, title, children, style }) => (
  <section className="cd-panel" style={ style }>
    { title &&
      <div className="cd-panel-head">
        { icon &&
          <span className="cd-panel-mark">
            <Icon icon={ icon } width={ 18 } />
          </span>
        }
        <h2 className="cd-panel-title">{ title }</h2>
      </div>
    }
    <div className="cd-panel-body">{ children }</div>
  </section>
);

const StatTile = ({ label, value }) => (
  <div className="cd-stat">
    <span className="cd-stat-label">{ label }</span>
    <span className="cd-stat-value">{ value ?? 0 }</span>
  </div>
);

/** Breadcrumb names the case once it is known, rather than just "Cases". */
const headersFor = (caseData) => ({
  title: "Case details",
  breadcrumb: (caseData && caseData.case_number)
    ? [{ name: "Cases", path: "/cases/total" }, { name: caseData.case_number }]
    : [{ name: "Cases", path: "/cases/total" }],
});

const UserCaseDetail = () => {
  const authUser = useSelector((state) => state.auth.authUser);
  const { refresh: refreshCaseCounts } = useCaseCounts();
  const { id } = useParams();
  const navigate = useNavigate()
  const [caseData, setCaseData] = useState([]);
  // True from the start: the fetch below runs in an effect, i.e. after
  // the first paint, so a `false` here rendered one frame of "This case
  // didn't load" before the request it is waiting on had even left.
  const [loader, setLoader] = useState(true);
  const [childComponent, setChildComponent] = useState(null);
  const [authCase, setAuthCase] = useState(false);
  const [imageDownload, setImageDownload] = useState(false);
  const [caseOrders, setCaseOrders] = useState(null);
  const [containers, setContainers] = useState([]);
  const [containerLoader, setContainerLoader] = useState(false);
  const confirm = useConfirm();

  // Every action on this page moves the case between named states, so a
  // confirmation says which case and which state rather than "Are you
  // sure?" — see BaseConfirm.
  const caseNumber = caseData.case_number || "this case";

  // `action` keeps the dialog open with its button working until the
  // request settles — this page has no loading state of its own past the
  // first render, so without it an approve looked like a dead click.
  const ask = (options, run) => confirm({ ...options, action: run });

  useEffect(() => {
    getCasesData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (caseData && caseData.status === 5) {
      getContainersData();
    }
  }, [caseData?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCasesData = () => {
    makeRequest(setLoader, getUserCaseDetailReq, id, onSuccess, onError);
  }

  const getContainersData = () => {
    makeRequest(setContainerLoader, getCaseContainersReq, id, onContainersSuccess, onContainersError);
  }

  const onContainersSuccess = (data) => {
    setContainers(data || []);
  }

  const onContainersError = (error) => {
    console.log(error);
  }

  const onUploadContainer = (file) => {
    const formData = new FormData();
    formData.append('case_id', id);
    formData.append('file', file);
    makeRequest(setContainerLoader, addCaseContainerReq, formData, onContainerAddSuccess, onContainerAddError);
    return false; // tell antd Upload not to auto-upload the file itself
  }

  const onContainerAddSuccess = () => {
    toast.success("Container file added successfully");
    getContainersData();
  }

  const onContainerAddError = (error, res) => {
    toast.error(res?.msg || "Unable to add container file");
  }

  const onDeleteContainer = (containerId) => {
    return makeRequest(setContainerLoader, deleteCaseContainerReq, containerId, onContainerDeleteSuccess, onContainerDeleteError);
  }

  const onContainerDeleteSuccess = () => {
    toast.success("Container file removed");
    getContainersData();
  }

  const onContainerDeleteError = (error, res) => {
    toast.error(res?.msg || "Unable to remove container file");
  }

  const onError = (error) => {
    console.log(error);
  }

  const onSuccess = (data) => {
    // Opening the case marked it read on the server, so this user's
    // sidebar bubbles can drop it now rather than on the next poll.
    refreshCaseCounts();
    if (data.taken_by === authUser.id) {
      setAuthCase(true);
    } else {
      setAuthCase(false);
    }
    setCaseData(data);
    let tempOrders = [];
    tempOrders.push({
      key: '1', label: <div style={ { display: 'flex' } }><strong>Aligners order { moment(data.created_at).format('DD MMM YYYY hh:mm') }</strong></div>, children: caseInformation(data, imageDownload, setImageDownload)
    });
    data.treatmentplan && data.treatmentplan.length > 0 && data.treatmentplan.forEach((each, index) => {
      tempOrders.push({
        key: index + 2, label: <div style={ { display: 'flex' } }><strong>Treatment Plan { moment(each.created_at).format('DD MMM YYYY hh:mm') }</strong></div>, children: caseTreatmentInformation(each, data, authUser)
      });
    })
    tempOrders.push({
      key: 'discussions', label: <div style={ { display: 'flex' } }><strong>Discussions</strong></div>, children: caseDiscussion(data)
    });

    setCaseOrders(tempOrders);

  }

  const onTakeJob = () => {
    makeRequest(setLoader, takeUserCasReq, id, onTakeSuccess, onTakeError);
  }

  const handleAmendReason = () => {
    setChildComponent(<AmendCaseComponent onSuccess={ onAmendSuccess } caseid={ id } handleCancel={ onCancel } />)
  }

  const onAmendSuccess = () => {
    setChildComponent(null);
    getCasesData();
    toast.success("Case Amended Successfully");
  }

  const onTakeSuccess = (data) => {
    getCasesData();
    toast.success("Case Taken Successfully");
  }

  const onTakeError = (error, res) => {
    toast.error(res?.msg || "Case Already Taken");
  }

  const onAcceptCase = () => {
    return makeRequest(setLoader, acceptCaseReq, id, onAcceptSuccess, onAcceptError);
  }

  const onAcceptSuccess = (data) => {
    getCasesData();
    toast.success("Case Accepted Successfully");
  }

  const onAcceptError = (error, res) => {
    toast.error(res?.msg || "Case Already Accepted");
  }

  const onCompleteCase = () => {
    return makeRequest(setLoader, completeCaseReq, id, onCompleteSuccess, onCompleteError);
  }

  const onCompleteSuccess = () => {
    getCasesData();
    toast.success("Case Completed Successfully");
  }

  const onCompleteError = (error, res) => {
    toast.error(res?.msg || "Unable to complete this case");
  }

  const handleRejectReason = () => {
    setChildComponent(<RejectCaseComponent onSuccess={ onSuccessReject } caseid={ id } handleCancel={ onCancel } />)
  }



  const handleCancelReason = () => {
    setChildComponent(<CancelCaseComponent onSuccess={ onCancelCaseSuccess } caseid={ id } handleCancel={ onCancel } />)
  }

  const onCancelCaseSuccess = () => {
    getCasesData();
    setChildComponent(null);
    toast.success("Case put on hold — it now shows under On Hold");
  }

  const onSuccessReject = () => {
    getCasesData();
    setChildComponent(null);
    toast.success("Case Rejected Successfully");
  }

  const onResolveMissingData = () => {
    return makeRequest(setLoader, resolveMissingDataReq, id, onResolveMissingDataSuccess, onResolveMissingDataError);
  }

  const onResolveMissingDataSuccess = () => {
    getCasesData();
    toast.success("Case moved back to Pending — it can now be taken again");
  }

  const onResolveMissingDataError = (error, res) => {
    toast.error(res?.msg || "Unable to take this case off hold");
  }


  /**
   * Open the lightbox on the thumbnail that was clicked, not on the
   * first one in the set.
   */
  const ViewImages = (images, startIndex = 0) => {
    setChildComponent(
      <ViewImagesModal images={ images } startIndex={ startIndex } onClosed={ handleCloseModal } />
    );
  }

  const handleCloseModal = () => {
    setChildComponent(null);
  }

  const view3DSetup = (link) => {
    if (!link) {
      toast.info("No 3D setup link available for this case");
      return;
    }
    setChildComponent(<View3DSetup link={ link } onClosed={ handleCloseModal } />);
  }

  /**
   * The files come down as the zip itself rather than as a link to one
   * left lying in the server's public directory, so it is saved from a
   * blob here. makeRequest is not involved: it reads the app's JSON
   * envelope, and this response is a file.
   */
  const downloadImagesAsZip = async (caseinfo, setImageDownload) => {
    setImageDownload(true);

    const res = await getDownloadFilesReq(caseinfo.id);

    setImageDownload(false);

    if (!res || !res.blob) {
      toast.error((res && res.msg) || "Failed to download files");
      return;
    }

    // A refusal still arrives as a 200 carrying the app's JSON envelope,
    // so a zip that is actually JSON is an error message, not a file.
    if (String(res.headers?.['content-type'] || '').includes('application/json')) {
      let msg = "Failed to download files";
      try {
        msg = JSON.parse(await res.blob.text()).msg || msg;
      } catch (e) { /* keep the default */ }
      toast.error(msg);
      return;
    }

    const url = window.URL.createObjectURL(res.blob);
    const link = document.createElement('a');
    link.href = url;
    // Named from the case handed in, not from caseData: this handler is
    // captured when the order panel is built, before caseData is set, so
    // reading caseData here saved every case as the same "case_data.zip".
    link.setAttribute('download', `${caseinfo.case_number || `case-${caseinfo.id}`}_data.zip`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };




  /**
   * Everything a person can do to this case, in one bar.
   *
   * These were seven buttons scattered down a right-aligned column, each
   * with its own inline fontSize and hand-set red, so the destructive
   * ones looked identical in weight to the safe ones. The permission and
   * status gating below is unchanged - only the presentation is.
   */
  const caseActions = () => {
    const actions = [];

    if (isTechnicianRole() && caseData.status === 0) {
      actions.push(
        <ButtonComponent key="take" type="primary"
          icon={ <Icon icon="tabler:hand-grab" width={ 18 } /> }
          onClick={ onTakeJob }>Take this case</ButtonComponent>
      );
    }

    if (isTechnicianRole() && authCase && caseData.status === 1) {
      actions.push(
        <ButtonComponent key="abend" danger
          icon={ <Icon icon="tabler:x" width={ 18 } /> }
          onClick={ handleAmendReason }>Abandon case</ButtonComponent>
      );
    }

    if (checkPermission('cases_approve') && caseData.status === 2) {
      actions.push(
        <ButtonComponent key="accept" type="primary"
          icon={ <Icon icon="tabler:check" width={ 18 } /> }
          onClick={ () => ask({
            tone: 'warning',
            icon: 'tabler:check',
            title: 'Approve this treatment plan?',
            description: 'The technician will be told the plan is signed off and can start production.',
            outcome: `Case ${caseNumber} moves to Approved.`,
            confirmText: 'Approve',
            cancelText: 'Not yet',
          }, onAcceptCase) }>Approve</ButtonComponent>
      );
    }

    if (checkPermission('cases_reject') && caseData.status === 2) {
      actions.push(
        <ButtonComponent key="reject" danger
          icon={ <Icon icon="tabler:x" width={ 18 } /> }
          onClick={ handleRejectReason }>Reject</ButtonComponent>
      );
    }

    // Anyone working the case may close it out — doctor, technician and
    // admin alike. What gates completion is the STL being on the case,
    // not who is looking at it; the API enforces both.
    if (caseData.status === 4) {
      actions.push(
        caseData.file_link
          ? <ButtonComponent key="complete" type="primary"
            icon={ <Icon icon="tabler:circle-check" width={ 18 } /> }
            onClick={ () => ask({
              tone: 'warning',
              icon: 'tabler:circle-check',
              title: 'Mark this case as completed?',
              description: 'Completing closes the case. Treatment plans and files stay readable, but nothing further can be actioned on it.',
              outcome: `Case ${caseNumber} moves to Completed.`,
              confirmText: 'Complete case',
              cancelText: 'Not yet',
            }, onCompleteCase) }>Complete case</ButtonComponent>
          // Shown disabled rather than hidden: "where did the Complete
          // button go?" is a worse question than a button that says what
          // it is waiting for.
          : <Tooltip key="complete" title="Submit the STL file first — see the STL file panel below.">
            <span><ButtonComponent disabled
              icon={ <Icon icon="tabler:circle-check" width={ 18 } /> }>Complete case</ButtonComponent></span>
          </Tooltip>
      );
    }

    if (checkPermission('cases_approve') && caseData.refine_id === caseData.id) {
      actions.push(
        <ButtonComponent key="resolve" type="primary"
          icon={ <Icon icon="tabler:arrow-back-up" width={ 18 } /> }
          onClick={ () => ask({
            tone: 'warning',
            icon: 'tabler:arrow-back-up',
            title: 'Take this case off hold?',
            description: 'Do this once the data a technician asked for has been fixed.',
            outcome: `Case ${caseNumber} returns to Total Cases, unassigned — a technician will need to take it again.`,
            confirmText: 'Send back',
            cancelText: 'Not yet',
          }, onResolveMissingData) }>Data fixed, take off hold</ButtonComponent>
      );
    }

    if (checkPermission('cases_edit')) {
      actions.push(
        <ButtonComponent key="edit"
          icon={ <Icon icon="tabler:pencil" width={ 18 } /> }
          onClick={ onEditCase }>Edit case</ButtonComponent>
      );
    }

    return actions;
  };

  const primaryDetail = () => {
    const photo = caseData.photo ? fileUrl(caseData.photo) : null;

    return (
      <div className="cd-hero">
        <div className="cd-hero-main">
          { photo
            ? <img className="cd-hero-photo" src={ photo } alt={ `Patient ${caseData.name || ''}` } />
            : <span className="cd-hero-photo is-placeholder">
              { (caseData.name || '?').charAt(0).toUpperCase() }
            </span> }

          <div className="cd-hero-text">
            <div className="cd-hero-title">
              <h1>{ caseData.name || 'Unknown patient' }</h1>
              <StatusTag status={ caseData.status } />
              { caseData.refine_id === caseData.id &&
                <span className="status-tag status-modification">Missing data</span> }
            </div>

            <p className="cd-hero-sub">
              <span className="mono">{ caseData.case_number }</span>
              { caseData.type?.name && <> &middot; { caseData.type.name }</> }
            </p>

            <dl className="cd-hero-meta">
              <div className="cd-meta-item">
                <dt>Gender</dt>
                <dd>{ caseData.gender || '--' }</dd>
              </div>
              <div className="cd-meta-item">
                <dt>Date of birth</dt>
                <dd>{ caseData.dob ? moment(caseData.dob).format('DD MMM YYYY') : '--' }</dd>
              </div>
              <div className="cd-meta-item">
                <dt>Submitted</dt>
                <dd>{ caseData.created_at ? moment(caseData.created_at).format('DD MMM YYYY, HH:mm') : '--' }</dd>
              </div>
              <div className="cd-meta-item">
                <dt>Taken by</dt>
                <dd>{ caseData.taken_by_user?.name || 'Nobody yet' }</dd>
              </div>
            </dl>

            { caseData.refine_id && caseData.refine_id !== caseData.id &&
              <Link className="cd-hero-link" to={ "/cases/details/" + caseData.refine_id } target="_blank">
                <Icon icon="tabler:external-link" width={ 16 } /> View reference case
              </Link> }
          </div>
        </div>

        <div className="cd-hero-actions">{ caseActions() }</div>
      </div>
    );
  };

  const caseInformation = (caseinfo, imageDownload, setImageDownload) => {
    return <>
      <h3 className="cd-h3">Special instructions from the doctor</h3>
      <div className="cd-box">
        <p><strong>{ caseinfo.doctor_instructions }</strong></p>

        <dl className="cd-facts">
          <div><dt>Case type</dt><dd>{ caseinfo.type?.name || '--' }</dd></div>
          {/* Admin-only: which country this case came from, i.e. the one
              the doctor it belongs to practises in. */}
          { authUser?.is_admin &&
            <div><dt>Country</dt><dd><CountryFlag code={ caseinfo.country } fallback="Not set" /></dd></div>
          }
          { caseinfo.arch_side &&
            <div><dt>Arch</dt><dd>{ caseinfo.arch_side === 'upper' ? 'Upper arch' : caseinfo.arch_side === 'lower' ? 'Lower arch' : '--' }</dd></div>
          }
          <div><dt>Improve overjet</dt><dd>{ caseinfo.overjet || '--' }</dd></div>
          <div><dt>Improve overbite</dt><dd>{ caseinfo.overbite || '--' }</dd></div>
          <div><dt>Improve crossbite</dt><dd>{ caseinfo.crossbite || '--' }</dd></div>
          <div><dt>Improve openbite</dt><dd>{ caseinfo.openbite || '--' }</dd></div>
          <div><dt>Improve midline</dt><dd>{ caseinfo.midline || '--' }</dd></div>
        </dl>
        <p>Additional Comments :   <strong>{ caseinfo.additional_comments }</strong></p>
      </div>

      <div className="cd-section-head">
        <div>
          <h3 className="cd-h3">Photos</h3>
        </div>
        <div>
          <ButtonComponent state={ imageDownload }
            icon={ <Icon icon="tabler:download" width={ 18 } /> }
            onClick={ () => downloadImagesAsZip(caseinfo, setImageDownload) }>Download all files</ButtonComponent>
        </div>
      </div>
      <div className="cd-box">
        <div className="cd-grid">
          { caseinfo.photos && caseinfo.photos.map((photo, index) => {
            const src = fileUrl(photo.path);
            return (
              <figure className="cd-thumb" key={ photo.id || index }>
                <button type="button" className="cd-thumb-frame" onClick={ () => ViewImages(caseinfo.photos, index) }>
                  <img src={ src } alt={ photo.name || 'Patient photo' } />
                </button>
                <figcaption>
                  <span className="cd-thumb-name">{ photo.name }</span>
                  <Tooltip title="Download">
                    <button type="button" className="cd-thumb-dl"
                      onClick={ () => handleDownload(src, photo.name) }
                      aria-label={ `Download ${photo.name}` }>
                      <Icon icon="tabler:download" width={ 17 } />
                    </button>
                  </Tooltip>
                </figcaption>
              </figure>
            );
          }) }
        </div>
      </div>

      <div className="cd-section-head">
        <h3 className="cd-h3">X-rays</h3>
      </div>
      <div className="cd-box">
        <div className="cd-grid">
          { caseinfo.xrays && caseinfo.xrays.map((photo, index) => (
            <figure className="cd-thumb" key={ photo.id || index }>
              <button type="button" className="cd-thumb-frame" onClick={ () => ViewImages(caseinfo.xrays, index) }>
                <img src={ fileUrl(photo.path) } alt={ photo.name || 'X-ray' } />
              </button>
              <figcaption>
                <span className="cd-thumb-name">{ photo.name }</span>
                <Tooltip title="Download">
                  <button type="button" className="cd-thumb-dl"
                    onClick={ () => handleDownload(fileUrl(photo.path), photo.name) }
                    aria-label={ `Download ${photo.name}` }>
                    <Icon icon="tabler:download" width={ 17 } />
                  </button>
                </Tooltip>
              </figcaption>
            </figure>
          )) }
        </div>
      </div>

      <h3 className="cd-h3">Scans</h3>
      <div className="cd-box">
        <div className="cd-grid">
          { caseinfo.scans && caseinfo.scans.map((scan, index) => (
            <figure className="cd-thumb is-file" key={ scan.id || index }>
              <span className="cd-thumb-frame">
                <img src={ ScanImage } alt="" />
              </span>
              <figcaption>
                <span className="cd-thumb-name">{ scan.name }</span>
                <Tooltip title="Download">
                  <button type="button" className="cd-thumb-dl"
                    onClick={ () => handleDownload(fileUrl(scan.path), scan.name) }
                    aria-label={ `Download ${scan.name}` }>
                    <Icon icon="tabler:download" width={ 17 } />
                  </button>
                </Tooltip>
              </figcaption>
            </figure>
          )) }
        </div>
      </div>

      { caseinfo.status === 1 && isTechnicianRole() &&
        <div className="cd-decide">
          <div className="cd-decide-text">
            <strong>Ready to plan this case?</strong>
            <span>Start the treatment plan, or put the case on hold if the submitted data does not meet the requirements.</span>
          </div>
          <div className="cd-actions">
            <ButtonComponent danger
              icon={ <Icon icon="tabler:alert-triangle" width={ 18 } /> }
              onClick={ handleCancelReason }>Put on hold</ButtonComponent>
            <ButtonComponent type="primary"
              icon={ <Icon icon="tabler:clipboard-plus" width={ 18 } /> }
              onClick={ () => navigate("/cases/details/treatment-plan/" + id) }>Create treatment plan</ButtonComponent>
          </div>
        </div> }
    </>
  }

  const handleDownloadPdf = (treatmentplan, caszeData) => {
    setChildComponent(<SafePdfComponent treatmentplan={ treatmentplan } caseData={ caszeData } onClosed={ onCancel } />)
  }



  const onCancel = () => {
    setChildComponent(null)
  }

  const onEditCase = () => {
    const record = { ...caseData };
    record.dob = record.dob ? moment(record.dob) : record.dob;
    record.case_type = Number(record.case_type);
    setChildComponent(<UpdateCase data={ record } onUpdated={ onCaseUpdated } onClosed={ onCancel } />)
  }

  const onCaseUpdated = () => {
    setChildComponent(null);
    getCasesData();
    // No toast here: the update drawer already reports the outcome, and
    // names the case while it does it. Both firing put two stacked
    // "case updated" toasts on screen for one save.
  }


  /**
   * Who may rewrite the treatment plan.
   *
   * Two people: an admin, and the technician who took the case. Neither
   * is held to a particular status — a plan is correctable for as long
   * as the case is open, which is the rule the API already applies
   * (updateUserCaseTreatmentPlan asks only that the caller can see the
   * case). This screen used to hide the button from the technician
   * everywhere except status 3, so one who spotted their own mistake a
   * minute after submitting had no way to fix it.
   *
   * A completed case (5) is closed to both. That is the app's own rule,
   * stated on the Complete button: nothing further can be actioned on
   * it. Saving an approved plan puts the case back to 2 for the doctor
   * to look at again, which is the point of the edit.
   */
  const canEditPlan = (casezData) => {
    if (casezData.status === 5) return false;
    return isAdmin() || casezData.taken_by === authUser.id;
  };

  /**
   * Whether a *second* plan may be written alongside the one on screen,
   * as opposed to the one on screen being corrected.
   *
   * This mirrors createUserCaseTreatmentPlan's own rule, which refuses a
   * new plan with "Treatment Plan already exists for this case" unless
   * the case is back with the technician for changes (3) or is a
   * refinement round still waiting for its plan (1). Offering the button
   * anywhere else — which showing it wherever Edit plan shows would do —
   * hands the user a button whose only outcome is that error.
   */
  const canCreatePlan = (casezData) => {
    if (! canEditPlan(casezData)) return false;
    if (casezData.status === 3) return true;
    return casezData.status === 1 && !!casezData.refinement_started_at;
  };

  const caseTreatmentInformation = (treatmentInfo, casezData, authUser) => {
    return <div>
      <div className="cd-plan-head">
        <p className="cd-muted">Type of malocclusion, method of treatment, and the number of aligners required.</p>
        <div className="cd-actions">
          { canCreatePlan(casezData) &&
            <ButtonComponent type="primary"
              icon={ <Icon icon="tabler:plus" width={ 18 } /> }
              onClick={ () => navigate("/cases/details/treatment-plan/" + id) }>New treatment plan</ButtonComponent> }
          { canEditPlan(casezData) &&
            <ButtonComponent
              icon={ <Icon icon="tabler:pencil" width={ 18 } /> }
              onClick={ () => navigate("/cases/details/treatment-plan/update/" + treatmentInfo.id) }>Edit plan</ButtonComponent> }
          {/* Always offered. Reading a plan back is not an edit, and the
              technician who could edit one was the only person the
              screen refused to hand a PDF. */}
          <ButtonComponent
            icon={ <Icon icon="tabler:file-download" width={ 18 } /> }
            onClick={ () => handleDownloadPdf(treatmentInfo, casezData) }>Save plan as PDF</ButtonComponent>
        </div>
      </div>

      <SectionCard icon="tabler:layers-intersect" title="Aligners">
        <Row gutter={ [16, 16] }>
          <Col xs={ 24 } md={ 12 }><StatTile label="Aligners to the upper jaw" value={ treatmentInfo && treatmentInfo.aligner_upper_jaw } /></Col>
          <Col xs={ 24 } md={ 12 }><StatTile label="Aligners to the lower jaw" value={ treatmentInfo && treatmentInfo.aligner_lower_jaw } /></Col>
          <Col xs={ 24 } md={ 12 }><StatTile label="Templates for attachments to the upper jaw" value={ treatmentInfo && treatmentInfo.attachment_upper_jaw } /></Col>
          <Col xs={ 24 } md={ 12 }><StatTile label="Templates for attachments to the lower jaw" value={ treatmentInfo && treatmentInfo.attachment_lower_jaw } /></Col>
        </Row>
      </SectionCard>

      <SectionCard icon="tabler:stethoscope" title="Orthodontist notes">
        <div dangerouslySetInnerHTML={ safeHtml(treatmentInfo && treatmentInfo.orthodontist_notes) } />
      </SectionCard>

      <SectionCard icon="tabler:notes" title="Technician's notes">
        <div dangerouslySetInnerHTML={ safeHtml(treatmentInfo && treatmentInfo.technician_notes) } />
      </SectionCard>

      { treatmentInfo && treatmentInfo.iprsattachment && parseJsonList(treatmentInfo.iprsattachment.treatment_steps).map((each, index) =>
        <SectionCard key={ index } icon="tabler:tooth" title={ `Treatment step: ${index + 1}` }>
          <div style={ { width: '100%', display: 'flex', justifyContent: 'center' } }>
            <div style={ { width: '50%' } }>
              <TeethIcon disableAction={ true } selectedAttachments={ parseJsonList(treatmentInfo.iprsattachment.attachments) } stepIndex={ index } iprs={ parseJsonList(treatmentInfo.iprsattachment.iprs) } />
            </div>
          </div>
          <p style={ { textAlign: 'center', marginTop: 12 } }>From { each.from } to { each.to } aligner</p>
        </SectionCard>
      ) }

      <SectionCard icon="tabler:cube-3d-sphere" title="3D Report">
        <div className="cd-centre">
          <ButtonComponent type="primary" size="large"
            icon={ <Icon icon="tabler:cube-3d-sphere" width={ 20 } /> }
            onClick={ () => view3DSetup(treatmentInfo && treatmentInfo["3d_Setup_link"]) }>View 3D setup</ButtonComponent>
        </div>
      </SectionCard>

    </div>
  }

  // Pulled out of caseTreatmentInformation into its own top-level
  // accordion panel: the discussion is about the case as a whole, not
  // any one treatment plan version, and it used to be buried at the
  // bottom of whichever treatment plan panel happened to be open.
  const caseDiscussion = (casezData) => {
    return <div className="cd-discussions">
      { casezData?.discussions && casezData.discussions.length > 0
        ? casezData.discussions.map((msg, index) => (
          <div className="cd-message" key={ msg.id || index }>
            <span className="cd-message-avatar">
              { (msg.name || '?').charAt(0).toUpperCase() }
            </span>
            <div className="cd-message-body">
              <div className="cd-message-meta">
                <span className="cd-message-author">{ msg.name }</span>
                <Tooltip title={ moment(msg.created_at).format('DD MMM YYYY, HH:mm') }>
                  <span className="cd-message-when">{ moment(msg.created_at).fromNow() }</span>
                </Tooltip>
              </div>
              <div className="cd-message-text"
                dangerouslySetInnerHTML={ safeHtml(msg.message) } />
              {/* A file sent in the case chat — the STL arrives this
                  way, so it has to be reachable from the case too. */}
              { msg.attachment &&
                <button type="button" className="cd-file-chip"
                  onClick={ () => handleDownload(fileUrl(msg.attachment.path), msg.attachment.name) }>
                  <Icon icon="tabler:paperclip" width={ 15 } />
                  <span>{ msg.attachment.name }</span>
                  <Icon icon="tabler:download" width={ 15 } />
                </button> }
            </div>
          </div>
        ))
        : <p className="cd-muted">No discussion on this case yet.</p> }
    </div>
  }


  // Nothing renders against a half-empty object any more: until the first
  // response lands the page shows a skeleton of its own layout.
  const ready = caseData && caseData.id;

  if (loader && !ready) {
    return (
      <>
        <HeaderComponent headers={ headersFor(null) } />
        <CaseDetailSkeleton />
      </>
    );
  }

  if (!ready) {
    return (
      <>
        <HeaderComponent headers={ headersFor(null) } />
        <div className="cd-page">
          <BaseError
            title="This case didn't load"
            description="The case could not be fetched. It may have been removed, or the server didn't answer."
            onRetry={ getCasesData }
          />
        </div>
      </>
    );
  }

  return (
    <>
      { childComponent }
      <HeaderComponent
        headers={ headersFor(caseData) }
        description={ caseData.name ? `Submitted for ${caseData.name}.` : null }
      />
      <div className="cd-page">
        { primaryDetail() }

        {/* whoever is looking - doctor or technician - sees why the case
            came back and who sent it */ }
        <CaseReasonBanner caseData={ caseData } />

        { caseData && caseData.case_type &&
          <Collapse
            accordion
            size="large"
            expandIconPosition="right"
            defaultActiveKey={ caseData && caseData.treatmentplan ? [`${caseData.treatmentplan.length + 1}`] : ["1"] }
            items={ caseOrders }
          />
        }

        {/* Approved is the point the case is waiting to be handed over,
            so the STL sits here from then on — as the thing still to do
            while it is missing, and as the delivered file afterwards.
            It stays visible on a completed case: that link is the
            deliverable, and people come back for it. */}
        { caseData && (caseData.status === 4 || caseData.status === 5) &&
          <SectionCard icon="tabler:cube" title="STL file">
            <CaseStlDelivery caseData={ caseData } onDelivered={ getCasesData } />
          </SectionCard>
        }

        { caseData && caseData.status === 5 &&
          <SectionCard icon="tabler:box" title="Container files">
            {/* The cap used to be announced by rewriting the button's own
                label to "Maximum of 2 container files reached", so the
                control stopped saying what it does and said why it can't.
                The button keeps its name; the count sits beside it. */ }
            {/* Doctor, technician and admin all handle containers, so
                there is no permission gate here — only the backend's
                case-access check. */ }
            <div className="cd-containers-bar">
              <Upload
                showUploadList={ false }
                disabled={ containers.length >= 2 || containerLoader }
                beforeUpload={ onUploadContainer }
              >
                <ButtonComponent disabled={ containers.length >= 2 } state={ containerLoader }>
                  <Icon icon="tabler:upload" style={ { marginRight: 5 } } />
                  Add container file
                </ButtonComponent>
              </Upload>

              <span className="cd-containers-count">
                { containers.length >= 2
                  ? "Both slots used — remove one to add another."
                  : `${containers.length} of 2 added` }
              </span>
            </div>

            { containers.length === 0
              ? <Text type="secondary">No container files added yet.</Text>
              : containers.map((c) => (
                <Row key={ c.id } align="middle" style={ { padding: '10px 0', borderBottom: '1px solid #f0f0f0' } }>
                  <Col flex="auto">
                    <Icon icon="tabler:file-text" style={ { marginRight: 8, verticalAlign: 'middle' } } />
                    { c.name }
                    { c.uploaded_by_user?.name &&
                      <Text type="secondary" style={ { marginLeft: 10, fontSize: 12 } }>by { c.uploaded_by_user.name }</Text>
                    }
                  </Col>
                  <Col>
                    <Tooltip title="Download">
                      <Icon
                        onClick={ () => handleDownload(fileUrl(c.path), c.name) }
                        style={ { fontSize: 20, cursor: 'pointer', color: '#1677ff', marginRight: 16 } }
                        icon="line-md:downloading-loop"
                      />
                    </Tooltip>
                    <Tooltip title="Remove">
                      <Icon
                        style={ { color: 'var(--danger)', cursor: 'pointer' } } width={ 18 } icon="tabler:trash"
                        onClick={ () => ask({
                          tone: 'danger',
                          icon: 'tabler:trash',
                          title: 'Remove this container file?',
                          description: `"${c.name}" will be deleted from case ${caseNumber}.`,
                          outcome: 'Deleting is permanent — the file cannot be recovered.',
                          confirmText: 'Remove file',
                          cancelText: 'Keep it',
                        }, () => onDeleteContainer(c.id)) }
                      />
                    </Tooltip>
                  </Col>
                </Row>
              ))
            }
          </SectionCard>
        }
      </div>
    </>
  )

}

export default UserCaseDetail